import classNames from 'classnames';
import React from 'react';

type Props = {
  id: string,
  title: string,
  tooltip?: string,
  icon: string,
  collapse: string,
  onlyShowIconWhenCollapsed?: boolean,
  allowLargeText?: boolean,
  originalText?: boolean,
  children?: React.ReactNode,
  className?: string,
  hideCaret?: boolean
};

function _renderLink (
  id: string,
  icon: string,
  title: string,
  tooltip: string | undefined,
  collapse: string,
  onlyShowIconWhenCollapsed: boolean,
  allowLargeText: boolean,
  originalText: boolean,
  hideCaret: boolean,
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
): React.ReactNode {
  const collapseArray: string[] = collapse ? collapse.split(',') : [];

  const classesExpanded: string = classNames({
    'esko_visible-xs-inline': !collapseArray.includes('xs'),
    'esko_visible-sm-inline': !collapseArray.includes('sm'),
    'esko_visible-md-inline': !collapseArray.includes('md'),
    'esko_visible-lg-inline': !collapseArray.includes('lg'),
    'esko_navbaroriginaltext': originalText
  });
  const classesCollapsed: string = classNames({
    'esko_visible-xs-inline': collapseArray.includes('xs'),
    'esko_visible-sm-inline': collapseArray.includes('sm'),
    'esko_visible-md-inline': collapseArray.includes('md'),
    'esko_visible-lg-inline': collapseArray.includes('lg')
  });

  if (icon != null) {
    if (title != null) {
      if (onlyShowIconWhenCollapsed) {
        return (
          <a
            id={id}
            className={
              'esko_dropdown-toggle ' + (allowLargeText ? 'esko_navbaritemtext-large' : 'esko_navbaritemtext')
            }
            role='button'
            aria-haspopup='true'
            aria-expanded='false'
            title={tooltip}
            onClick={onClick}
          >
            <div className={classesExpanded}>
              {title}
            </div>
            <div className={classesCollapsed}>
              <span className={icon} />
            </div>
            {hideCaret ? null : <span className='esko_caret' />}
          </a>
        );
      } else {
        return (
          <a
            id={id}
            className={
              'esko_dropdown-toggle ' + (allowLargeText ? 'esko_navbaritemtext-large' : 'esko_navbaritemtext')
            }
            role='button'
            aria-haspopup='true'
            aria-expanded='false'
            title={tooltip}
            onClick={onClick}
          >
            <div className={classesExpanded}>
              <span className={icon} /> {title}
            </div>
            <div className={classesCollapsed}>
              <span className={icon} />
            </div>
            {hideCaret ? null : <span className='esko_caret' />}
          </a>
        );
      }
    } else if (hideCaret) {
      return (
        <a
          id={id}
          className='esko_dropdown-toggle esko_navbaritemtext'
          role='button'
          aria-haspopup='true'
          aria-expanded='false'
          title={tooltip}
          onClick={onClick}
        >
          <span className={icon} />
        </a>
      );
    } else {
      return (
        <a
          id={id}
          className='esko_dropdown-toggle esko_navbaritemtext'
          role='button'
          aria-haspopup='true'
          aria-expanded='false'
          title={tooltip}
          onClick={onClick}
        >
          <span className={icon} /> <span className='esko_caret' />
        </a>
      );
    }
  } else {
    return (
      <a
        id={id}
        className='dropdown-toggle esko_navbaritemtext'
        role='button'
        aria-haspopup='true'
        aria-expanded='false'
        title={tooltip}
        onClick={onClick}
      >
        {title} <span className='esko_caret' />
      </a>
    );
  }
}

type DefaultProps = Required<Pick<Props, 'onlyShowIconWhenCollapsed' | 'allowLargeText' | 'originalText'>>;

type PropsWithDefaults = Omit<Props, keyof DefaultProps> & DefaultProps;

type State = {
  dropType: string,
  mode: string,
  openMenuId: number
};

class NavigationBarMenu extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    onlyShowIconWhenCollapsed: false,
    allowLargeText: false,
    originalText: false
  };

  constructor (props: Props) {
    super(props);

    this.state = {
      dropType: 'esko_dropdown',
      mode: '',
      openMenuId: 0
    };

    this.handleDropdownClick = this.handleDropdownClick.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
  }
  
  handleMouseDown () {
    if (this.state.mode !== '') {
      const openMenuIdOnBlur: number = this.state.openMenuId;
      setTimeout(() => {
        if (this.state.mode !== '' && this.state.openMenuId === openMenuIdOnBlur) {
          this.setState({mode: ''});

          window.removeEventListener('mousedown', this.handleMouseDown);
        }
      }, 200);
    }
  }

  handleDropdownClick (event: React.MouseEvent<HTMLAnchorElement>) {
    if (this.state.mode !== 'open') {
      this.setState({mode: 'open', openMenuId: this.state.openMenuId + 1});
      
      window.addEventListener('mousedown', this.handleMouseDown);
    } else {
      this.setState({mode: ''});
      event.stopPropagation();

      window.removeEventListener('mousedown', this.handleMouseDown);
    }
  }

  override render (): React.ReactNode {
    const {
      id, title, tooltip, icon, collapse,
      onlyShowIconWhenCollapsed,
      allowLargeText,
      originalText,
      children, className, hideCaret
    } = (this.props as PropsWithDefaults);

    return (
      <li className={classNames('esko_dropdown', this.state.mode !== '' ? 'esko_' + this.state.mode : null, className)}>
        {_renderLink(
          id,
          icon,
          title,
          tooltip,
          collapse,
          onlyShowIconWhenCollapsed,
          allowLargeText,
          originalText,
          hideCaret === true,
          this.handleDropdownClick
        )}
        <ul className='esko_dropdown-menu'>{children}</ul>
      </li>
    );
  }
}

export default NavigationBarMenu;
